import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CopyButton, StatusPill } from '@ui-kit';
import moduleVersionsJson from '../../common-showcase/data/module-versions.json';
import type { ModuleVersions } from '../../common-showcase/types/showcase';
import { consumerGuideUrl } from '../lib/consumerGuideLink';
import { NEXUS_PUBLIC_URL, NEXUS_IS_PLACEHOLDER } from '../data/nexusRepository';

/**
 * ManualQuickstart — queue #644 §UAT-2026-07-07-R644.
 *
 * `docs/consumer-guide.md` §2(㉮ Nexus 경로)의 실동작 절차를 페이지 안 복사 가능한
 * step-by-step 섹션으로 승격한 5단계: ① settings.xml → ② pom(BOM import + 모듈
 * dependency) → ③ application.yml → ④ 샘플 컨트롤러(공통 ApiResponse 재사용) →
 * ⑤ 기동 확인 curl. 순수 정적 콘텐츠(BE 무호출) — 5상태 중 default 만 발생한다(§R644-5).
 *
 * ★ 버전 SSOT — #637 `common-showcase/data/module-versions.json` 을 cross-feature import
 * (CurationBundlePicker 가 take-this.json 을 쓰는 선례와 동형). 하드코딩 상수 재도입 금지 —
 * 프리빌드 산출물 미생성 시 이 import 자체가 실패해 빌드가 깨진다(회귀 방지, §R644-3).
 *
 * ★ 코드블록 본문(xml/yml/java/curl)은 언어 공통이라 i18n 대상이 아니다 — 제목·설명·복사
 * 버튼 라벨만 ko/en 대칭(§R644-6).
 */

const MODULE_VERSIONS = moduleVersionsJson as unknown as ModuleVersions;

// consumer-guide.md §2.1 과 동일 문안(§R644-7 drift 방지) — <url> 만 #670 §R670-4 로
// NEXUS_PUBLIC_URL(빌드타임 VITE_NEXUS_URL 주입값)로 동적 구성한다. 소스에 공인 IP 0.
function buildSettingsXml(nexusUrl: string): string {
  return `<!-- ~/.m2/settings.xml -->
<settings>
  <profiles>
    <profile>
      <id>eraf-nexus</id>
      <repositories>
        <repository>
          <id>nexus-public</id>
          <url>${nexusUrl}</url>
          <releases><enabled>true</enabled></releases>
          <snapshots><enabled>true</enabled></snapshots>
        </repository>
      </repositories>
    </profile>
  </profiles>
  <activeProfiles>
    <activeProfile>eraf-nexus</activeProfile>
  </activeProfiles>
  <!-- Add a <servers> block if authentication is required (id must match the repository id above; never commit credentials) -->
</settings>`;
}

/** 블록 A — consumer-guide.md §2.2 와 동형 구조 · <version> 은 #637 SSOT(bomVersion) 동적 렌더 */
function buildBomImportSnippet(bomVersion: string): string {
  return `<dependencyManagement>
  <dependencies>
    <dependency>
      <groupId>com.skax.eraf</groupId>
      <artifactId>eraf-bom</artifactId>
      <version>${bomVersion}</version>
      <type>pom</type>
      <scope>import</scope>
    </dependency>
  </dependencies>
</dependencyManagement>`;
}

/** 블록 B — consumer-guide.md §2.3 과 동형 구조 · 버전은 생략하고 주석에 실버전(SSOT) 부기 */
function buildModuleDependencySnippet(coreVersion: string | undefined): string {
  const comment = coreVersion
    ? `<!-- version omitted — managed by eraf-bom (${coreVersion}) -->`
    : `<!-- version omitted — managed by eraf-bom -->`;
  return `<dependencies>
  <dependency>
    <groupId>com.skax.eraf</groupId>
    <artifactId>eraf-core</artifactId>
    ${comment}
  </dependency>
</dependencies>`;
}

// eraf.common.exception.enabled 는 matchIfMissing=true(기본 활성) — 생략 가능하지만 이 최소
// yml 은 그 사실을 명시적으로 보여준다(docs/common-specs/eraf-core/C001-exception-handling.md §5).
const APPLICATION_YML = `# src/main/resources/application.yml
spring:
  datasource:
    url: jdbc:postgresql://localhost:5432/mydb
    username: myuser
    password: \${DB_PASSWORD}

eraf:
  common:
    exception:
      enabled: true   # default — auto-enabled even if omitted (matchIfMissing=true)
      # See the 'Take this' panel in the capability catalog for per-module opt-in settings (/showcase)`;

// §R644-2 샘플 컨트롤러 예시 그대로(공통 ApiResponse=C002 재사용, 재구현 없음)
const HELLO_CONTROLLER = `// com.acme.foo.hello.HelloController  (your package)
@RestController
@RequestMapping("/api/eraf")
public class HelloController {
    @GetMapping("/hello")
    public ApiResponse<String> hello() {         // ApiResponse = eraf-core (C002) — reuse via import, do not reimplement
        return ApiResponse.success("eraf up");
    }
}`;

const BOOT_CURL = `./mvnw spring-boot:run

curl -s localhost:8080/api/eraf/hello
# {"success":true,"data":"eraf up","error":null,"traceId":"c1a2b3d4-..."}`;

const codeBlockStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--spacing-3)',
  overflowX: 'auto',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  margin: 0,
};

interface QuickstartBlock {
  label?: string;
  code: string;
  /** #654 §R654-3 — Nexus IP 는 사내망(SG 화이트리스트) 전용이라 코드 옆에 배지로 명시. */
  internalOnly?: boolean;
  /** #670 §R670-4 — VITE_NEXUS_URL 미주입 시(placeholder 좌표) '자사 Nexus 로 교체' 배지. */
  placeholderBadge?: boolean;
}

interface QuickstartStep {
  title: string;
  desc: string;
  blocks: QuickstartBlock[];
}

export function ManualQuickstart() {
  const { t } = useTranslation();
  const guideUrl = consumerGuideUrl();

  const bomVersion = MODULE_VERSIONS.bomVersion;
  const coreVersion = MODULE_VERSIONS.modules['eraf-core'];

  const steps: QuickstartStep[] = [
    {
      title: t('gettingStartedGuide.quickstart.step1.title'),
      desc: t('gettingStartedGuide.quickstart.step1.desc'),
      blocks: [{ code: buildSettingsXml(NEXUS_PUBLIC_URL), internalOnly: true, placeholderBadge: NEXUS_IS_PLACEHOLDER }],
    },
    {
      title: t('gettingStartedGuide.quickstart.step2.title'),
      desc: t('gettingStartedGuide.quickstart.step2.desc'),
      blocks: [
        { label: 'eraf-bom import', code: buildBomImportSnippet(bomVersion) },
        { label: 'eraf-core dependency', code: buildModuleDependencySnippet(coreVersion) },
      ],
    },
    {
      title: t('gettingStartedGuide.quickstart.step3.title'),
      desc: t('gettingStartedGuide.quickstart.step3.desc'),
      blocks: [{ code: APPLICATION_YML }],
    },
    {
      title: t('gettingStartedGuide.quickstart.step4.title'),
      desc: t('gettingStartedGuide.quickstart.step4.desc'),
      blocks: [{ code: HELLO_CONTROLLER }],
    },
    {
      title: t('gettingStartedGuide.quickstart.step5.title'),
      desc: t('gettingStartedGuide.quickstart.step5.desc'),
      blocks: [{ code: BOOT_CURL }],
    },
  ];

  return (
    <section id="gsg-quickstart" aria-labelledby="gsg-quickstart-heading">
      <h2
        id="gsg-quickstart-heading"
        style={{ margin: '0 0 var(--spacing-1) 0', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
      >
        {t('gettingStartedGuide.quickstart.heading')}
      </h2>
      <p style={{ margin: '0 0 var(--spacing-4) 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
        {t('gettingStartedGuide.quickstart.intro')}
      </p>

      <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
        {steps.map((step, i) => (
          <li
            key={i}
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
            }}
          >
            <h3 style={{ margin: '0 0 var(--spacing-1) 0', fontSize: 'var(--font-size-md)', color: 'var(--color-text-primary)' }}>
              {step.title}
            </h3>
            <p style={{ margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              {step.desc}
            </p>
            {step.blocks.map((block, bi) => (
              <div key={bi} style={{ marginBottom: bi < step.blocks.length - 1 ? 'var(--spacing-3)' : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-1)' }}>
                  {(block.label || block.internalOnly || block.placeholderBadge) && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                      {block.label && (
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                          {block.label}
                        </span>
                      )}
                      {block.internalOnly && (
                        <StatusPill tone="neutral">{t('gettingStartedGuide.quickstart.internalOnlyBadge')}</StatusPill>
                      )}
                      {block.placeholderBadge && (
                        <StatusPill tone="warning">{t('gettingStartedGuide.quickstart.nexusPlaceholderBadge')}</StatusPill>
                      )}
                    </span>
                  )}
                  <CopyButton
                    text={block.code}
                    ariaLabel={t('gettingStartedGuide.quickstart.copyAria', { step: step.title })}
                    copyLabel={t('gettingStartedGuide.quickstart.copy')}
                    copiedLabel={t('gettingStartedGuide.quickstart.copied')}
                  />
                </div>
                <pre style={codeBlockStyle}>
                  <code>{block.code}</code>
                </pre>
              </div>
            ))}
          </li>
        ))}
      </ol>

      <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        {t('gettingStartedGuide.quickstart.consumerGuideNote')}
        {' — '}
        <Link to={guideUrl} style={{ color: 'var(--color-info)' }}>
          {t('gettingStartedGuide.paths.status.viewGuide')}
        </Link>
      </p>
    </section>
  );
}
